import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import {
  Box,
  TextField,
  Button,
  CircularProgress,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Paper,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { toast } from "react-toastify";
import {
  deleteChecklistPergunta,
  getChecklistGrupoById,
  putChecklistGrupo,
  putChecklistPergunta,
} from "../../api/editChecklistApi";
import { ChecklistGrupo, ChecklistPergunta } from "../../types/editChecklist";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";
import { Delete } from "@mui/icons-material";

type FormValues = {
  descricao_checklist_grupo: string;
  ajuda_checklist_grupo?: string | null;
  imagem_checklist_grupo?: string | null;
  rodape_checklist_grupo?: string | null;
  ordem_checklist_grupo?: number;
};

const EditarGrupo: React.FC = () => {
  const { grupoId } = useParams<{ grupoId: string }>();
  const id = Number(grupoId);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const { control, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: {} as FormValues,
  });
  const [grupo, setGrupo] = useState<ChecklistGrupo | null>(null);
  const [perguntas, setPerguntas] = useState<ChecklistPergunta[]>([]);

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("token") || "";
        const data = await getChecklistGrupoById(token, id);
        setGrupo(data);

        reset({
          descricao_checklist_grupo: data.descricao_checklist_grupo ?? "",
          ajuda_checklist_grupo: data.ajuda_checklist_grupo ?? "",
          imagem_checklist_grupo: data.imagem_checklist_grupo ?? "",
          rodape_checklist_grupo: data.rodape_checklist_grupo ?? "",
          ordem_checklist_grupo: data.ordem_checklist_grupo ?? 0,
        });

        const orderedPerguntas = [...(data.perguntas || [])].sort(
          (a, b) =>
            (a.ordem_checklist_pergunta ?? 0) -
            (b.ordem_checklist_pergunta ?? 0)
        );
        setPerguntas(orderedPerguntas);
      } catch (err) {
        console.error(err);
        toast.error("Erro ao buscar grupo");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [id, reset]);

  const onSubmit = async (form: FormValues) => {
    try {
      const token = localStorage.getItem("token") || "";
      await putChecklistGrupo(token, id, form);
      toast.success("Grupo atualizado");
      navigate(`/checklist/${grupo?.id_checklist}/grupos`);
    } catch (err) {
      console.error(err);
      toast.error("Erro ao atualizar grupo");
    }
  };

  const handleDragEndPerguntas = async (result: DropResult) => {
    if (!result.destination) return;

    const newPerguntas = Array.from(perguntas);
    const [moved] = newPerguntas.splice(result.source.index, 1);
    newPerguntas.splice(result.destination.index, 0, moved);

    const updated = newPerguntas.map((p, index) => ({
      ...p,
      ordem_checklist_pergunta: index + 1,
    }));

    setPerguntas(updated);

    try {
      const token = localStorage.getItem("token") || "";
      await Promise.all(
        updated.map((p) =>
          putChecklistPergunta(token, p.id, {
            ordem_checklist_pergunta: p.ordem_checklist_pergunta,
          })
        )
      );
      toast.success("Ordem das perguntas atualizada!");
    } catch (err) {
      console.error(err);
      toast.error("Erro ao salvar nova ordem das perguntas");
    }
  };

  const handleDeletePergunta = async (perguntaId: number) => {
    if (!window.confirm("Tem certeza que deseja excluir esta pergunta?"))
      return;

    try {
      const token = localStorage.getItem("token") || "";
      await deleteChecklistPergunta(token, perguntaId);

      setPerguntas((prev) => prev.filter((p) => p.id !== perguntaId));

      toast.success("Pergunta removida com sucesso!");
    } catch (err) {
      console.error(err);
      toast.error("Erro ao remover grupo");
    }
  };

  if (loading)
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );

  return (
    <Box sx={{ p: 3, maxWidth: 900 }}>
      <Typography variant="h5">Editar Grupo #{id}</Typography>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name="descricao_checklist_grupo"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <TextField
              {...field}
              label="Descrição do Grupo"
              fullWidth
              margin="normal"
            />
          )}
        />
        <Controller
          name="ajuda_checklist_grupo"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Ajuda do Grupo"
              fullWidth
              margin="normal"
            />
          )}
        />
        <Controller
          name="imagem_checklist_grupo"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Imagem (URL/base64)"
              fullWidth
              margin="normal"
            />
          )}
        />
        <Controller
          name="rodape_checklist_grupo"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Rodapé do Grupo"
              fullWidth
              margin="normal"
            />
          )}
        />
        <Controller
          name="ordem_checklist_grupo"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              type="number"
              label="Ordem"
              fullWidth
              margin="normal"
            />
          )}
        />

        <Box mt={2} display="flex" gap={2}>
          <Button variant="contained" type="submit">
            Salvar Grupo
          </Button>
          <Button variant="outlined" onClick={() => navigate(-1)}>
            Voltar
          </Button>
        </Box>
      </form>

      <Box mt={4}>
        <Box
          mb={1}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h6">Perguntas (arraste para ordenar)</Typography>

          <Button
            variant="outlined"
            onClick={() =>
              navigate(`/editarChecklist/grupo/${id}/pergunta/novo`)
            }
          >
            Adicionar nova pergunta
          </Button>
        </Box>

        <DragDropContext onDragEnd={handleDragEndPerguntas}>
          <Droppable droppableId="perguntas-list">
            {(provided) => (
              <List
                {...provided.droppableProps}
                ref={provided.innerRef}
                sx={{ width: "100%", maxWidth: 800 }}
              >
                {perguntas.map((p, index) => (
                  <Draggable
                    key={p.id.toString()}
                    draggableId={p.id.toString()}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <Paper
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        sx={{
                          mb: 1,
                          p: 1,
                          display: "flex",
                          alignItems: "center",
                          background: snapshot.isDragging ? "#f0f0f0" : "white",
                        }}
                      >
                        <Box
                          {...provided.dragHandleProps}
                          sx={{
                            pr: 2,
                            display: "flex",
                            alignItems: "center",
                            color: "gray",
                            cursor: "grab",
                          }}
                        >
                          <DragIndicatorIcon />
                        </Box>
                        <ListItem
                          sx={{ width: "100%", alignItems: "flex-start" }}
                          secondaryAction={
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <IconButton
                                onClick={() =>
                                  navigate(`/editarChecklist/pergunta/${p.id}`)
                                }
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                edge="end"
                                aria-label="delete"
                                color="error"
                                onClick={() => handleDeletePergunta(p.id)}
                              >
                                <Delete />
                              </IconButton>
                            </Box>
                          }
                        >
                          <ListItemText
                            primary={`${p.ordem_checklist_pergunta}. ${p.descricao_checklist_pergunta}`}
                            secondary={
                              <>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Obrigatória:{" "}
                                  {p.obrigatorio_checklist_pergunta
                                    ? "Sim"
                                    : "Não"}
                                </Typography>

                                {p.respostas && p.respostas.length > 0 ? (
                                  <Box mt={0.5}>
                                    {p.respostas.map((r) => (
                                      <Typography
                                        key={r.id}
                                        variant="body2"
                                        color="text.secondary"
                                      >
                                        {`${r.ordem_checklist_resposta}. ${r.descricao_checklist_resposta} (Valor: ${r.valor_checklist_respostas})`}
                                      </Typography>
                                    ))}
                                  </Box>
                                ) : (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    mt={0.5}
                                  >
                                    Nenhuma resposta cadastrada
                                  </Typography>
                                )}
                              </>
                            }
                          />
                        </ListItem>
                      </Paper>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </List>
            )}
          </Droppable>
        </DragDropContext>
      </Box>
    </Box>
  );
};

export default EditarGrupo;
